import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, ValidationErrors, AbstractControl } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { JJamesAlertComponent, JJamesInputComponent, JJamesCardComponent, JjamesButtonComponent, JJamesLoadingComponent, Role } from 'shared-lib';
import { RoleService } from '../../auth/role-service';
import { ParkingSpaceService } from '../../../api/gen/api/parkingSpace.service';
import { ParkingSpaceDto } from '../../../api/gen/model/parkingSpaceDto';
import { EditParkingSpaceRequest } from '../../../api/gen/model/editParkingSpaceRequest';
import { ParkingSpaceType } from '../../../api/gen/model/parkingSpaceType';
import { ParkingAccessMode } from '../../../api/gen/model/parkingAccessMode';
import { ParkingEntryType } from '../../../api/gen/model/parkingEntryType';
import { OrganizationService } from '../../../api/gen/api/organization.service';
import { OrganizationDto } from '../../../api/gen/model/organizationDto';
import { AccessWindowFieldComponent, AccessWindowValue } from '../../shared/access-window-field/access-window-field.component';

/**
 * `EditParkingSpaceRequest` plus the two fields the backend accepts but the generated client does
 * not know about yet. Both are sent as ordinary body properties, so this compiles today and starts
 * working the moment the API is up.
 *
 * Delete this alias and use the generated type directly once operator-app's OpenAPI client has been
 * regenerated.
 */
type EditSpaceRequest = EditParkingSpaceRequest & {
  allowFakeDriveIn?: boolean | null;
  hotline?: string | null;
  exitPaymentPresenceWindowMinutes?: number | null;
};

/**
 * Fields whose value decides what happens to a car at a barrier. Getting one wrong does not produce
 * an error — it produces parkers denied entry, trapped at an exit, or let out for free, discovered
 * hours later. They are locked to Administrators.
 *
 * This is a guard-rail against an accidental edit, not a security boundary: `PUT /parkingSpace/{id}`
 * accepts every one of these from any operator role. Enforcing it server-side would mean carrying
 * the caller's roles into the command, which is a separate change.
 */
const ADMIN_ONLY_FIELDS = [
  'accessMode',
  'entryType',
  'allowFakeDriveIn',
  'oldParkingSiteId',
  'externalOrganizationId',
  'qrPaymentEnabled',
  'exitPaymentPresenceWindowMinutes',
  'fiscalTransactionLocation',
  'fiscalTransactionTerminal'
] as const;

/**
 * The EFSTA register is both halves or neither: one alone would pair a custom TL with the proxy's
 * default TT and open a register nobody has onboarded with a signature device.
 */
function fiscalRegisterPair(form: AbstractControl): ValidationErrors | null {
  const location = (form.get('fiscalTransactionLocation')?.value ?? '').trim();
  const terminal = (form.get('fiscalTransactionTerminal')?.value ?? '').trim();
  return (location === '') === (terminal === '') ? null : { fiscalRegisterPair: true };
}

@Component({
  selector: 'app-edit-parkingspace',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    JjamesButtonComponent,
    JJamesInputComponent,
    JJamesCardComponent,
    JJamesAlertComponent,
    JJamesLoadingComponent,
    AccessWindowFieldComponent
  ],
  templateUrl: './edit-parkingspace.component.html',
  styleUrl: './edit-parkingspace.component.scss'
})
export class EditParkingspaceComponent implements OnInit {
  editSpaceForm: FormGroup;
  isSubmitting = false;
  isLoading = true;
  errorMessage: string | null = null;
  successMessage: string | null = null;
  parkingSpaceId: string | null = null;
  currentSpace: ParkingSpaceDto | null = null;

  parkingSpaceTypes = Object.values(ParkingSpaceType);
  accessModes = Object.values(ParkingAccessMode);
  entryTypes = Object.values(ParkingEntryType);

  /**
   * Organisations that can govern this site. Picking one means SSO logins at this space are
   * enrolled as members of it — which is the prerequisite for an operator to place them in a pool.
   * It grants no parking by itself.
   */
  organizations = signal<OrganizationDto[]>([]);

  /** Optional restricted-access (visitor) window. Empty = no window. Applies to Closed spaces. */
  restrictedWindow = signal<AccessWindowValue>({ start: null, end: null, days: null });

  /** Resolved from Keycloak. Until it resolves the barrier-affecting fields stay locked. */
  isAdmin = signal(false);

  /** The restricted-access window only applies to Closed spaces, so only surface it for those. */
  get isClosedSpace(): boolean {
    return this.editSpaceForm.get('accessMode')?.value === ParkingAccessMode.Closed;
  }

  constructor(
    private fb: FormBuilder,
    private parkingSpaceService: ParkingSpaceService,
    private organizationService: OrganizationService,
    private roleService: RoleService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.editSpaceForm = this.createForm();
    // Locked until proven otherwise: a slow or failed role lookup must not hand a member the
    // barrier-affecting fields.
    this.applyRoleRestrictions();
  }

  /**
   * Disable or re-enable the admin-only controls to match {@link isAdmin}.
   *
   * Disabled controls are dropped from `form.value`, which is why the submit reads `getRawValue()` —
   * a member's save has to send the values back unchanged, not send nothing and let the backend's
   * "null means leave alone" rule stand in for a decision nobody made.
   */
  private applyRoleRestrictions(): void {
    for (const name of ADMIN_ONLY_FIELDS) {
      const control = this.editSpaceForm.get(name);
      if (!control) continue;
      if (this.isAdmin()) control.enable({ emitEvent: false });
      else control.disable({ emitEvent: false });
    }
  }

  private async resolveRole(): Promise<void> {
    this.isAdmin.set(await this.roleService.checkAccess(Role.Administrator));
    this.applyRoleRestrictions();
  }

  private loadOrganizations(): void {
    this.organizationService.organizationsFilterPost({ pageIndex: 0, pageSize: 200, includeDeleted: false })
      .subscribe({
        next: (response) => this.organizations.set(response?.items ?? []),
        // A failed lookup must not block editing the rest of the space — the field just stays empty.
        error: () => this.organizations.set([])
      });
  }

  ngOnInit(): void {
    void this.resolveRole();
    this.loadOrganizations();
    this.parkingSpaceId = this.route.snapshot.paramMap.get('id');
    if (this.parkingSpaceId) {
      this.loadParkingSpace();
    } else {
      this.router.navigate(['/parking-spaces']);
    }
  }

  private createForm(): FormGroup {
    return this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      description: ['', [Validators.maxLength(500)]],
      parkingSpaceType: [ParkingSpaceType.Standard, [Validators.required]],
      accessMode: [ParkingAccessMode.Open, [Validators.required]],
      entryType: [ParkingEntryType.FreeFlow, [Validators.required]],
      oldParkingSiteId: [null],
      // EFSTA EFR register (TL/TT pair). Blank both = the proxy's default register. Sent as a pair:
      // one half without the other would pair a custom TL with the default TT and open an unboarded
      // register. Both-or-neither is enforced by the fiscalRegisterPair validator.
      fiscalTransactionLocation: ['', [Validators.maxLength(46)]],
      fiscalTransactionTerminal: ['', [Validators.maxLength(46)]],
      // Offer the QR/Stripe pay channel at this space's exit. Off unless a site actually has a
      // display to show the code on — with it on and no display, the parker sees nothing.
      qrPaymentEnabled: [false],
      // Pay-at-exit presence window override, minutes. Blank = use the global default (15);
      // 0 = clear the override (back to the global default); 1..1440 = set it.
      exitPaymentPresenceWindowMinutes: [null, [Validators.min(0), Validators.max(1440)]],
      // Old-backend site setting: let a car out that has no recorded entry.
      allowFakeDriveIn: [true],
      // Shown on this site's payment stations. Empty = advertise no phone number.
      hotline: ['', [Validators.maxLength(32)]],
      // Blank = no limit. Min 1: zero would lock every student out of the webshop entirely.
      maxSelfServiceLicensePlates: [null, [Validators.min(1)]],
      // Cars inside at once per Dauerparkrecht, pushed onto every parker pool here. Separate from the
      // plate cap above. Blank = leave unchanged. Min 1: zero would turn every Dauerparker short-term.
      maxConcurrentCarsPerParkerPool: [null, [Validators.min(1)]],
      // Blank = no limit. Min 1: zero would mean "changeable instantly", i.e. no cooldown at all —
      // clear the field instead of setting 0.
      licensePlateChangeCooldownDays: [null, [Validators.min(1)]],
      // Empty = operator-governed, which is what every space is unless told otherwise.
      externalOrganizationId: [null],
      firstAddressLine: ['', [Validators.maxLength(200)]],
      secondAddressLine: ['', [Validators.maxLength(200)]],
      locality: ['', [Validators.maxLength(100)]],
      postCode: ['', [Validators.maxLength(20)]],
      region: ['', [Validators.maxLength(100)]],
      countryCode: ['', [Validators.maxLength(10)]]
    }, { validators: fiscalRegisterPair });
  }

  private loadParkingSpace(): void {
    if (!this.parkingSpaceId) return;

    this.isLoading = true;
    this.parkingSpaceService.parkingSpaceParkingSpaceIdGet(this.parkingSpaceId).subscribe({
      next: (space: ParkingSpaceDto) => {
        this.currentSpace = space;
        this.populateForm(space);
        this.isLoading = false;
      },
      error: (error: any) => {
        this.errorMessage = 'Failed to load parking space details.';
        this.isLoading = false;
        console.error('Error loading parking space:', error);
      }
    });
  }

  private populateForm(space: ParkingSpaceDto): void {
    this.editSpaceForm.patchValue({
      name: space.name || '',
      description: space.description || '',
      parkingSpaceType: space.type || ParkingSpaceType.Standard,
      accessMode: space.accessMode || ParkingAccessMode.Open,
      entryType: space.entryType || ParkingEntryType.FreeFlow,
      oldParkingSiteId: space.oldParkingSiteId ?? null,
      fiscalTransactionLocation: space.fiscalTransactionLocation ?? '',
      fiscalTransactionTerminal: space.fiscalTransactionTerminal ?? '',
      qrPaymentEnabled: space.qrPaymentEnabled ?? false,
      // The generated DTO learns this field on the next API-client regen; read through a cast until
      // then so the form compiles today.
      exitPaymentPresenceWindowMinutes:
        (space as ParkingSpaceDto & { exitPaymentPresenceWindowMinutes?: number | null }).exitPaymentPresenceWindowMinutes ?? null,
      // Defaults to true on the entity, so an undefined here means "on", not "off".
      allowFakeDriveIn: space.allowFakeDriveIn ?? true,
      hotline: space.hotline ?? '',
      maxSelfServiceLicensePlates: space.maxSelfServiceLicensePlates ?? null,
      // Same cast as exitPaymentPresenceWindowMinutes above until the next API-client regen.
      maxConcurrentCarsPerParkerPool:
        (space as ParkingSpaceDto & { maxConcurrentCarsPerParkerPool?: number | null }).maxConcurrentCarsPerParkerPool ?? null,
      licensePlateChangeCooldownDays: space.licensePlateChangeCooldownDays ?? null,
      externalOrganizationId: space.externalOrganizationId ?? null,
      firstAddressLine: space.address?.firstAddressLine || '',
      secondAddressLine: space.address?.secondAddressLine || '',
      locality: space.address?.locality || '',
      postCode: space.address?.postCode || '',
      region: space.address?.region || '',
      countryCode: space.address?.countryCode || ''
    });

    this.restrictedWindow.set({
      start: space.restrictedAccessWindowStart ?? null,
      end: space.restrictedAccessWindowEnd ?? null,
      days: space.restrictedAccessWindowDays ?? null
    });
  }

  onSubmit(): void {
    if (this.editSpaceForm.valid && this.parkingSpaceId) {
      this.isSubmitting = true;
      this.errorMessage = null;

      // getRawValue, not value: the admin-only controls are disabled for other roles and would
      // otherwise be absent here, turning "I did not touch this" into a null the backend reads as
      // "leave unchanged" — or, for the booleans, into a false that silently flips the setting.
      const formValue = this.editSpaceForm.getRawValue();
      // Window only applies to Closed spaces — clear it otherwise. Sent as a unit (replace semantics).
      const window = this.isClosedSpace ? this.restrictedWindow() : { start: null, end: null, days: null };
      // The intersection carries maxConcurrentCarsPerParkerPool until the next API-client regen adds it.
      const updateRequest: EditParkingSpaceRequest & { maxConcurrentCarsPerParkerPool?: number | null } = {
        name: formValue.name,
        description: formValue.description,
        parkingSpaceType: formValue.parkingSpaceType,
        accessMode: formValue.accessMode,
        entryType: formValue.entryType,
        oldParkingSiteId: formValue.oldParkingSiteId != null ? Number(formValue.oldParkingSiteId) : null,
        // Blank sends "" so the backend clears the register (null would mean "leave unchanged").
        fiscalTransactionLocation: formValue.fiscalTransactionLocation?.trim() || '',
        fiscalTransactionTerminal: formValue.fiscalTransactionTerminal?.trim() || '',
        qrPaymentEnabled: !!formValue.qrPaymentEnabled,
        allowFakeDriveIn: !!formValue.allowFakeDriveIn,
        // Blank = leave unchanged (global default applies); 0 = clear the override; >= 1 sets it.
        exitPaymentPresenceWindowMinutes:
          formValue.exitPaymentPresenceWindowMinutes === '' || formValue.exitPaymentPresenceWindowMinutes == null
            ? null
            : Number(formValue.exitPaymentPresenceWindowMinutes),
        // Sent even when empty: the backend reads "" as "clear it", which is the only way a site
        // that stops staffing a phone line can stop advertising one.
        hotline: formValue.hotline ?? '',
        // An empty field means "no limit"; the backend treats null as unchanged, so clearing an
        // existing cap is not expressible here yet — see the note in EditParkingSpaceHandler.
        maxSelfServiceLicensePlates: formValue.maxSelfServiceLicensePlates
          ? Number(formValue.maxSelfServiceLicensePlates)
          : null,
        // Same null-is-unchanged rule as the plate cap; a value re-syncs every parker pool here.
        maxConcurrentCarsPerParkerPool: formValue.maxConcurrentCarsPerParkerPool
          ? Number(formValue.maxConcurrentCarsPerParkerPool)
          : null,
        // Unlike the cap above, an empty field does clear the cooldown — the handler reads null as
        // "no cooldown". So this form is authoritative for the setting and must keep sending it.
        licensePlateChangeCooldownDays: formValue.licensePlateChangeCooldownDays
          ? Number(formValue.licensePlateChangeCooldownDays)
          : null,
        // Same caveat as the cap above: the backend reads null as "leave unchanged", so detaching a
        // site from its organisation is not expressible from this form yet.
        externalOrganizationId: formValue.externalOrganizationId || null,
        restrictedAccessWindowStart: window.start,
        restrictedAccessWindowEnd: window.end,
        restrictedAccessWindowDays: window.days,
        address: {
          firstAddressLine: formValue.firstAddressLine || null,
          secondAddressLine: formValue.secondAddressLine || null,
          locality: formValue.locality || null,
          postCode: formValue.postCode || null,
          region: formValue.region || null,
          countryCode: formValue.countryCode || null
        }
      };

      this.parkingSpaceService.parkingSpaceParkingSpaceIdPut(this.parkingSpaceId, updateRequest).subscribe({
        next: () => {
          this.successMessage = 'Parking space updated successfully!';
          this.isSubmitting = false;
          setTimeout(() => {
            this.router.navigate(['/parking-space', this.parkingSpaceId]);
          }, 2000);
        },
        error: (error: any) => {
          this.errorMessage = 'Failed to update parking space. Please try again.';
          this.isSubmitting = false;
          console.error('Error updating parking space:', error);
        }
      });
    } else {
      this.markFormGroupTouched();
    }
  }

  onCancel(): void {
    this.router.navigate(['/parking-space', this.parkingSpaceId]);
  }

  private markFormGroupTouched(): void {
    Object.keys(this.editSpaceForm.controls).forEach(key => {
      this.editSpaceForm.get(key)?.markAsTouched();
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.editSpaceForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.editSpaceForm.get(fieldName);
    if (field && field.errors) {
      if (field.errors['required']) return `${fieldName} is required`;
      if (field.errors['minlength']) return `${fieldName} must be at least ${field.errors['minlength'].requiredLength} characters`;
      if (field.errors['maxlength']) return `${fieldName} must not exceed ${field.errors['maxlength'].requiredLength} characters`;
    }
    return '';
  }
}
